/**
 * Plain-language readings for the Brief's numeric cards.
 *
 * A card that shows only "Slope +0.86" or "58% Advancing" asks the reader to
 * supply the meaning themselves. These helpers attach the "so what" line.
 *
 * Two rules hold everywhere in this file:
 *  - Every threshold lives HERE and is exported, so the label a card shows and
 *    the meaning printed beneath it are generated from one ladder. Two
 *    hand-written copies of a band table drift, and the drift is silent: the
 *    card would call a curve flat while the sentence called it steep.
 *  - A reading is a description of the data, never a forecast or a
 *    recommendation. Where a reading leans on a historical regularity, it says
 *    so and says what is unreliable about it.
 */

// Slope band, in percentage points of 10Y minus 3M, inside which the curve is
// treated as flat rather than sloped in either direction.
export const CURVE_FLAT_BAND = 0.1;

/**
 * The yield curve's shape and what it implies, from the 10Y-minus-3M spread.
 * Returns `null` for a missing slope so the caller drops the card rather than
 * rendering a confident sentence about an absent number.
 */
export const describeYieldCurve = (slope) => {
  if (typeof slope !== 'number' || !Number.isFinite(slope)) {
    return null;
  }
  if (slope < -CURVE_FLAT_BAND) {
    return {
      shape: 'inverted',
      summary: 'Yield curve inversion flashing caution',
      meaning:
        'Short-term yields sit above long-term ones — lenders are paid more to wait a few months than a decade. It has preceded most recessions, but the lead time has run anywhere from months to two years, so it dates nothing.',
    };
  }
  if (slope <= CURVE_FLAT_BAND) {
    return {
      shape: 'flat',
      summary: 'Yield curve is nearly flat',
      meaning:
        'Short and long rates have converged — the market is charging almost nothing extra to hold duration, which usually means growth and inflation expectations are finely balanced.',
    };
  }
  return {
    shape: 'positive',
    summary: 'Yield curve remains positively sloped',
    meaning:
      'Long rates sit above short ones, the ordinary shape — the market is pricing continued growth and is being paid to hold duration rather than bracing for a cut cycle.',
  };
};

// Share of advancing names, in percent, at which breadth stops reading as mixed.
export const BREADTH_BANDS = Object.freeze({
  broadAdvance: 65,
  modestAdvance: 55,
  modestDecline: 45,
  broadDecline: 35,
});

/**
 * What an advancing-share reading says about participation.
 *
 * Breadth answers "is the whole market moving, or a handful of names" — which
 * an index level cannot. `advancingPercent` is the share of counted names that
 * rose, 0-100.
 */
export const describeBreadth = (advancingPercent) => {
  if (typeof advancingPercent !== 'number' || !Number.isFinite(advancingPercent)) {
    return null;
  }
  if (advancingPercent >= BREADTH_BANDS.broadAdvance) {
    return 'Broad participation — most names are rising, not just the index heavyweights.';
  }
  if (advancingPercent >= BREADTH_BANDS.modestAdvance) {
    return 'More names up than down, though the advance is far from unanimous.';
  }
  if (advancingPercent > BREADTH_BANDS.modestDecline) {
    return 'Roughly an even split — direction at the index level is being set by size, not by the number of names moving.';
  }
  if (advancingPercent > BREADTH_BANDS.broadDecline) {
    return 'More names down than up — the weakness is spread rather than isolated.';
  }
  return 'Broadly negative — declines are widespread, so a flat index would be masking heavy damage underneath.';
};

/**
 * `topIndustriesIncreases` / `topIndustriesDecreases` from `daily-movement-analytics`
 * carry a `percentage` that is a SHARE OF CONSTITUENTS, always positive (the worker
 * drops anything under 5 and caps at 100) — the fraction of that industry's
 * actively-quoted names that moved in the given direction. It is NOT an average
 * price change.
 *
 * That distinction is the whole reason these helpers exist. Rendering the share with
 * a +/- sign turns "100% of its traded names fell" into "it fell 100%", which is a
 * different and far more dramatic claim about the same number. Always render the
 * share through `formatIndustryShare`, which names the direction in words instead.
 *
 * Note the neighbouring `daily_momentum` field, from the separate `sector-rotation`
 * worker, IS a signed mean price change and must keep its sign. Same-looking
 * "Industry (N%)" text, different metric — check which worker feeds a tile before
 * changing how its number reads.
 */

// The worker only emits an industry with >=15 constituents of which >=5 traded that
// session, so a 100% share rests on at least five names — small, but never one.
export const INDUSTRY_MIN_ACTIVE_CONSTITUENTS = 5;

export const formatIndustryShare = (percentage, direction) => {
  if (typeof percentage !== 'number' || !Number.isFinite(percentage)) return '';
  return `${Math.round(percentage)}% ${direction === 'up' ? 'advancing' : 'declining'}`;
};

export const describeIndustryShare = (direction) =>
  direction === 'up'
    ? `Share of each industry's actively-traded names that rose — a breadth reading, not an average price move. Industries need at least ${INDUSTRY_MIN_ACTIVE_CONSTITUENTS} names trading to qualify, so a high share can rest on a small sample.`
    : `Share of each industry's actively-traded names that fell — a breadth reading, not an average price move. Industries need at least ${INDUSTRY_MIN_ACTIVE_CONSTITUENTS} names trading to qualify, so a high share can rest on a small sample.`;
